import { useState } from "react";
import { toast } from "sonner";
import { FileCheck2, FileSignature } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CANCEL_REASONS, PIPELINE, STATUS, STATUS_GROUPS } from "@/lib/format";
import { useApi, errMsg } from "@/lib/api";

const groupColor = (s) => STATUS_GROUPS.find((g) => g.key === STATUS[s]?.group)?.color || "#9CA3AF";

export function StatusPipeline({ trip }) {
  const at = PIPELINE.indexOf(trip.status);
  if (trip.status === "cancelled") {
    return (
      <p className="inline-flex flex-wrap items-center gap-2 px-3 py-1.5 bg-zinc-200 text-zinc-700 text-xs" data-testid="trip-cancelled-chip">
        <span className="uppercase tracking-[0.18em] text-[0.62rem] font-semibold">Cancelled</span>
        {trip.cancel_reason && <span>{CANCEL_REASONS[trip.cancel_reason]}{trip.cancel_note ? ` · ${trip.cancel_note}` : ""}</span>}
      </p>
    );
  }
  return (
    <ol className="grid grid-cols-7 gap-1 w-full max-w-3xl" data-testid="trip-pipeline">
      {PIPELINE.map((s, i) => (
        <li key={s} className="min-w-0" data-testid={`pipeline-step-${s}`} data-state={i < at ? "done" : i === at ? "current" : "todo"}>
          <span className="block h-1.5" style={{ background: i <= at ? groupColor(s) : "rgba(120,120,120,0.25)" }} />
          <span className={`hidden sm:block mt-1.5 text-[0.58rem] tracking-[0.12em] uppercase truncate ${i === at ? "text-white font-semibold" : "text-zinc-500"}`}>{STATUS[s].label}</span>
        </li>
      ))}
      <li className="col-span-7 sm:hidden text-[0.62rem] tracking-[0.14em] uppercase text-zinc-400">Step {at + 1} of 7 · {STATUS[trip.status]?.label}</li>
    </ol>
  );
}

export function NextStep({ trip, staff, onChanged }) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const run = async (fn, done) => {
    setBusy(true);
    try { await fn(); toast.success(done); onChanged(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (trip.status === "options_sent") {
    return <p className="text-xs text-zinc-400" data-testid="next-step-hint">Next: tap "Client wants this one" on the option the client picks.</p>;
  }
  if (!staff && trip.status === "booking_requested") {
    return <p className="text-xs text-zinc-400" data-testid="next-step-hint">Booking requested. Our flight team is getting the operator contract signed.</p>;
  }
  const nextUnsigned = (trip.booked_option_ids?.length ? trip.booked_option_ids : [trip.booked_option_id]).find((x) => x && !(trip.contract_signed_ids || []).includes(x));
  if (staff && trip.status === "booking_requested" && nextUnsigned) {
    return (
      <button className="frame-btn gold" disabled={busy} onClick={() => run(() => api.post(`/options/${nextUnsigned}/contract-signed`), "Operator contract signed.")} data-testid="next-step-contract-signed-btn">
        <FileSignature size={13} /> Operator contract signed
      </button>
    );
  }
  if (staff && trip.status === "confirmed") {
    return (
      <button className="frame-btn gold" disabled={busy} onClick={() => run(() => api.patch(`/trips/${trip.id}`, { status: "itinerary_sent", notify: true }), "Itinerary sent. Broker notified.")} data-testid="next-step-itinerary-sent-btn">
        <FileCheck2 size={13} /> Itinerary sent
      </button>
    );
  }
  return null;
}

export function CancelTripDialog({ trip, open, onOpenChange, onSaved }) {
  const api = useApi();
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const change = (o) => {
    if (!o) { setReason(""); setNote(""); }
    onOpenChange(o);
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/trips/${trip.id}`, { status: "cancelled", cancel_reason: reason, cancel_note: note, notify: true });
      toast.success("Trip cancelled. Broker notified.");
      change(false);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent className="bg-[#111319] border-white/10 max-w-md" data-testid="cancel-trip-dialog">
        <DialogHeader>
          <p className="eyebrow">{trip.ref}</p>
          <DialogTitle className="font-serif-display text-3xl font-normal text-white">Cancel this trip?</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          {Object.entries(CANCEL_REASONS).map(([k, label]) => (
            <label key={k} className={`flex items-center gap-3 px-3 py-2.5 border cursor-pointer text-sm ${reason === k ? "border-[#C6A55C] text-white" : "border-white/10 text-zinc-300"}`}>
              <input type="radio" name="cancel-reason" value={k} checked={reason === k} onChange={() => setReason(k)} data-testid={`cancel-reason-${k}`} /> {label}
            </label>
          ))}
          <Textarea className="bg-[#0d0f14] border-white/10 min-h-[80px]" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder={reason === "other" ? "Tell us what happened (required)" : "Note (optional)"} data-testid="cancel-note-input" />
        </div>
        <div className="flex justify-end gap-3 mt-2">
          <button className="frame-btn" onClick={() => change(false)} data-testid="cancel-trip-back-btn">Back</button>
          <button className="frame-btn gold" disabled={busy || !reason || (reason === "other" && !note.trim())} onClick={save} data-testid="cancel-trip-confirm-btn">{busy ? "Saving…" : "Cancel trip"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
